/**
 * exam-bank/loader — 載入「真實段考題庫」並正規化成統一清單。
 *
 * 背景:老闆把拍照轉錄的實體段考真卷放進 `public/data/*-exam-bank-v1.json` /
 * `*-grammar-*-v1.json` / `english-vocab-lesson15-18.json`。各檔 schema 略不同
 * (nature 是 papers[].questions[];math / english 是 top-level questions[];
 * vocab 是 lessons[].words[])。本 loader 把它們攤平成統一的「卷」清單,讓
 * practice UI 不必認識各檔格式。
 *
 * 學習/遊戲分離鐵則:純學習側資料,絕不寫入 commanderXp / factionXP / 戰鬥數值。
 * 容錯:任一檔 fetch 失敗 / JSON 壞掉 / 結構不符 → 略過該檔,不讓整批爆掉。
 */

import type { Subject } from '../../shared/types';
import type { VocabWord } from '../../shared/vocab-store';

/** 統一後的「題目」欄位。各原始題型攤平到這組欄位上。 */
export interface NormQ {
  id: string;
  /** 原始題型 — 保留以決定 UI 渲染 / 是否可自動批改。 */
  kind: string;
  prompt: string;
  /** 選項(mcq / image-mcq 才有)。 */
  options?: string[];
  /**
   * 標準答案。型別依 kind:
   *  - tf: 'O' | 'X'
   *  - mcq / image-mcq: number(選項 index)
   *  - fill / compute / inverse / word: string
   *  - 其餘複雜題型:string(示範答案,給「看解答」對照)
   */
  answer?: string | number;
  /** 解析(可選)。 */
  explain?: string;
  /** check / match / fill-grid / reading 等複雜題型的子項原始資料。 */
  items?: unknown[];
  /** reading 題的文章本文。 */
  passage?: string;
  /** image-mcq 需要圖片才能作答(目前圖未補)。 */
  needsImage?: boolean;
  /** word 題的算式 / 單位(展示用)。 */
  expr?: string;
  unit?: string;
}

/** 一份「卷」(可作答)。 */
export interface ExamPaperEntry {
  kind: 'paper';
  id: string;
  subject: Subject;
  /** 顯示標題,如「自然·天氣特派員」。 */
  title: string;
  questions: NormQ[];
}

/** 英文單字聽寫項(接既有 runDictation)。 */
export interface VocabSetEntry {
  kind: 'vocab-set';
  id: string;
  subject: Subject;
  title: string;
  words: VocabWord[];
}

export type ExamPaperSummary = ExamPaperEntry | VocabSetEntry;

/** 可自動批改的題型(輸入即時對錯)。其餘只做「看解答」。 */
const AUTO_GRADED_KINDS = new Set(['tf', 'mcq', 'fill', 'compute', 'inverse', 'word']);

export function isAutoGraded(kind: string): boolean {
  return AUTO_GRADED_KINDS.has(kind);
}

const SUBJECT_LABEL: Record<Subject, string> = {
  math: '數學',
  chinese: '國語',
  english: '英文',
  nature: '自然',
  social: '社會',
};

/** 各題庫檔的 fetch 路徑 + 解析器。解析失敗回 [](該檔被略過)。 */
interface BankSource {
  url: string;
  parse: (raw: unknown) => ExamPaperSummary[];
}

const SOURCES: BankSource[] = [
  { url: '/data/nature-exam-bank-v1.json', parse: parseNatureBank },
  { url: '/data/math-exam-bank-v1.json', parse: parseMathBank },
  { url: '/data/english-grammar-bank-v1.json', parse: parseGrammarBank },
  { url: '/data/english-grammar-familyfriends-v1.json', parse: parseGrammarBank },
  { url: '/data/chinese-exam-bank-v1.json', parse: parseChineseBank },
  { url: '/data/english-vocab-lesson15-18.json', parse: parseVocabBank },
];

/**
 * 載入並正規化所有題庫。任一檔失敗都不影響其他檔(容錯逐檔 try/catch)。
 */
export async function loadExamBanks(): Promise<ExamPaperSummary[]> {
  const results = await Promise.all(
    SOURCES.map(async (src) => {
      try {
        const resp = await fetch(src.url);
        if (!resp.ok) return [];
        const raw = (await resp.json()) as unknown;
        return src.parse(raw);
      } catch {
        // 檔缺 / 網路錯 / JSON 壞 / 結構不符 → 略過該檔。
        return [] as ExamPaperSummary[];
      }
    }),
  );
  return results.flat();
}

// ── helpers ─────────────────────────────────────────────────────────────────

function asArray(v: unknown): unknown[] {
  return Array.isArray(v) ? v : [];
}

function asString(v: unknown): string | undefined {
  return typeof v === 'string' ? v : undefined;
}

/** 把一筆原始 question 物件攤平成 NormQ。缺 id/kind/prompt 者回 null(略過)。 */
function normQuestion(raw: unknown): NormQ | null {
  if (typeof raw !== 'object' || raw === null) return null;
  const o = raw as Record<string, unknown>;
  const id = asString(o.id);
  const kind = asString(o.kind);
  const prompt = asString(o.prompt) ?? asString(o.passage);
  if (!id || !kind || !prompt) return null;

  const q: NormQ = { id, kind, prompt };

  if (Array.isArray(o.options)) {
    q.options = o.options.map((opt) => String(opt));
  }
  if (typeof o.answer === 'string' || typeof o.answer === 'number') {
    q.answer = o.answer;
  }
  if (typeof o.explain === 'string') q.explain = o.explain;
  if (typeof o.passage === 'string') q.passage = o.passage;
  if (o.needsImage === true) q.needsImage = true;
  if (typeof o.expr === 'string') q.expr = o.expr;
  if (typeof o.unit === 'string') q.unit = o.unit;

  // 複雜題型的子項原始資料(check.items / match.items / fill-grid.items /
  // reading.subQuestions)— 原樣帶過去給「看解答」渲染。
  if (Array.isArray(o.items)) q.items = o.items;
  else if (Array.isArray(o.subQuestions)) q.items = o.subQuestions;
  else if (Array.isArray(o.correct) || o.codes !== undefined) {
    // check 題:items 是字串陣列 + correct index 陣列;一起塞進 items 給看解答。
    q.items = Array.isArray(o.items) ? o.items : [];
  }

  return q;
}

// ── per-file parsers ──────────────────────────────────────────────────────────

/** nature: { subject, papers[].{paperId, unit, questions[]} } */
function parseNatureBank(raw: unknown): ExamPaperSummary[] {
  if (typeof raw !== 'object' || raw === null) return [];
  const o = raw as Record<string, unknown>;
  const papers = asArray(o.papers);
  const out: ExamPaperSummary[] = [];
  for (const p of papers) {
    if (typeof p !== 'object' || p === null) continue;
    const pp = p as Record<string, unknown>;
    const paperId = asString(pp.paperId);
    if (!paperId) continue;
    const questions = asArray(pp.questions)
      .map(normQuestion)
      .filter((q): q is NormQ => q !== null);
    if (questions.length === 0) continue;
    const unit = asString(pp.unit) ?? '';
    out.push({
      kind: 'paper',
      id: paperId,
      subject: 'nature',
      title: `${SUBJECT_LABEL.nature}·${unit || paperId}`,
      questions,
    });
  }
  return out;
}

/** math: { subject, unit, source, questions[] } — 整檔一份卷。 */
function parseMathBank(raw: unknown): ExamPaperSummary[] {
  if (typeof raw !== 'object' || raw === null) return [];
  const o = raw as Record<string, unknown>;
  const questions = asArray(o.questions)
    .map(normQuestion)
    .filter((q): q is NormQ => q !== null);
  if (questions.length === 0) return [];
  const unit = asString(o.unit) ?? '';
  return [
    {
      kind: 'paper',
      id: 'math-exam-bank-v1',
      subject: 'math',
      title: `${SUBJECT_LABEL.math}·${unit || '段考卷'}`,
      questions,
    },
  ];
}

/** english grammar / familyfriends: { subject:'english', unit, source, questions[] } */
function parseGrammarBank(raw: unknown): ExamPaperSummary[] {
  if (typeof raw !== 'object' || raw === null) return [];
  const o = raw as Record<string, unknown>;
  const questions = asArray(o.questions)
    .map(normQuestion)
    .filter((q): q is NormQ => q !== null);
  if (questions.length === 0) return [];
  const unit = asString(o.unit) ?? '';
  // id 取 source 或 unit 的穩定字串(同檔多次載入 dedup 由上層自然分開卡片)。
  const id = `english-grammar-${hashId(asString(o.source) ?? unit)}`;
  return [
    {
      kind: 'paper',
      id,
      subject: 'english',
      title: `${SUBJECT_LABEL.english}·${unit || '文法練習'}`,
      questions,
    },
  ];
}

/**
 * chinese: { subject:'chinese', unit, questions[] }。國語特有題型映射到可渲染的 kind:
 *  - word-bank-fill → fill(可自動批改;詞庫附在 prompt 後)
 *  - zhuyin-word / make-sentence → 看解答(注音+造詞 / 造句示範)
 *  - mcq / reading → 沿用既有處理
 */
function parseChineseBank(raw: unknown): ExamPaperSummary[] {
  if (typeof raw !== 'object' || raw === null) return [];
  const o = raw as Record<string, unknown>;
  const questions = asArray(o.questions)
    .map(normChineseQ)
    .filter((q): q is NormQ => q !== null);
  if (questions.length === 0) return [];
  const unit = asString(o.unit) ?? '';
  return [
    {
      kind: 'paper',
      id: 'chinese-exam-bank-v1',
      subject: 'chinese',
      title: `${SUBJECT_LABEL.chinese}·${unit || '段考卷'}`,
      questions,
    },
  ];
}

/** 把一筆國語題正規化成 NormQ(處理 word-bank-fill / zhuyin-word 等特有欄位)。 */
function normChineseQ(raw: unknown): NormQ | null {
  if (typeof raw !== 'object' || raw === null) return null;
  const o = raw as Record<string, unknown>;
  const id = asString(o.id);
  const kind = asString(o.kind);
  const prompt = asString(o.prompt) ?? asString(o.passage);
  if (!id || !kind || !prompt) return null;

  if (kind === 'word-bank-fill') {
    const bank = asArray(o.wordBank).map((w) => String(w));
    return {
      id,
      kind: 'fill', // 可自動批改:輸入比對 answer
      prompt: bank.length ? `${prompt}（詞庫:${bank.join('、')}）` : prompt,
      answer: asString(o.answer),
      explain: asString(o.explain),
    };
  }
  if (kind === 'zhuyin-word') {
    const zy = asString(o.zhuyin) ?? '';
    const ex = asString(o.wordExample) ?? '';
    return { id, kind: 'zhuyin-word', prompt, answer: `注音:${zy}｜造詞:${ex}` };
  }
  if (kind === 'mcq') {
    return {
      id,
      kind: 'mcq',
      prompt,
      options: asArray(o.options).map((opt) => String(opt)),
      answer: typeof o.answer === 'number' ? o.answer : 0,
      explain: asString(o.explain),
    };
  }
  if (kind === 'reading') {
    return {
      id,
      kind: 'reading',
      prompt: asString(o.passage) ?? prompt,
      passage: asString(o.passage),
      items: asArray(o.subQuestions),
      answer: asString(o.answer),
    };
  }
  // make-sentence 及其他 → 看解答
  return { id, kind, prompt, answer: asString(o.answer), explain: asString(o.explain) };
}

/** vocab: { subject:'english', lessons[].words[] } — 攤平成單一聽寫 vocab-set。 */
function parseVocabBank(raw: unknown): ExamPaperSummary[] {
  if (typeof raw !== 'object' || raw === null) return [];
  const o = raw as Record<string, unknown>;
  const lessons = asArray(o.lessons);
  const words: VocabWord[] = [];
  for (const l of lessons) {
    if (typeof l !== 'object' || l === null) continue;
    const ws = asArray((l as Record<string, unknown>).words);
    for (const w of ws) {
      if (typeof w !== 'object' || w === null) continue;
      const wo = w as Record<string, unknown>;
      const word = asString(wo.word);
      if (!word) continue;
      words.push({ word, zh: asString(wo.zh) ?? '' });
    }
  }
  if (words.length === 0) return [];
  return [
    {
      kind: 'vocab-set',
      id: 'english-vocab-lesson15-18',
      subject: 'english',
      title: `${SUBJECT_LABEL.english}·單字聽寫(Lesson 15-18)`,
      words,
    },
  ];
}

/** 簡短穩定 hash(djb2),只用來組 id,不需密碼學強度。 */
function hashId(s: string): string {
  let h = 5381;
  for (let i = 0; i < s.length; i++) {
    h = ((h << 5) + h + s.charCodeAt(i)) >>> 0;
  }
  return h.toString(36);
}
